import { N8nModule } from '../../types';

export const module1: N8nModule = {
  id: 'make-intro',
  title: 'Introduction to Make (Integromat)',
  description: 'Get started with Make and learn visual automation design.',
  content: `# Introduction to Make (formerly Integromat)

## What is Make?

Make (formerly Integromat) is a visual automation platform that allows you to design, build, and automate workflows between apps and services. With its visual interface, you can see exactly how data flows through your automation.

## Key Concepts

### Scenarios
A **Scenario** is Make's term for an automated workflow. Scenarios consist of:
- **Modules**: Individual steps in your automation
- **Routes**: Branching paths based on conditions
- **Connections**: Links between your apps and Make

### Modules
Modules are the building blocks of scenarios:
- **Triggers**: Start your automation (Instant or Scheduled)
- **Actions**: Perform operations in apps
- **Tools**: Transform, filter, and manipulate data
- **Aggregators**: Combine multiple items
- **Iterators**: Process arrays of data

### Visual Editor
Make's drag-and-drop interface shows:
- Flow direction and connections
- Data mapping between modules
- Conditional routing
- Error handling paths

## Getting Started

### Creating Your First Scenario

1. **Choose a Trigger Module**
   - Instant triggers (webhooks, watches)
   - Scheduled triggers (timers)
   - Poll-based triggers

2. **Add Action Modules**
   - Drag modules onto the canvas
   - Connect modules with flow lines
   - Configure each module's settings

3. **Map Data Fields**
   - Click on input fields
   - Select data from previous modules
   - Use functions and transformations

4. **Test Your Scenario**
   - Run test scenarios
   - View execution history
   - Debug with detailed logs

5. **Activate Your Scenario**
   - Turn on the scenario
   - Set scheduling options
   - Monitor executions

## Make vs Other Platforms

### Advantages of Make

1. **Visual Workflow Design**
   - See entire automation at a glance
   - Understand data flow visually
   - Easy to modify and extend

2. **Advanced Data Manipulation**
   - Built-in functions and formulas
   - Array operations
   - Complex transformations

3. **Powerful Routing**
   - Multiple conditional paths
   - Filters between any modules
   - Fallback routes

4. **Detailed Execution Logs**
   - See exact data at each step
   - Debug issues easily
   - Track operation history

## Common Use Cases

### Data Synchronization
- Sync contacts between CRMs
- Update databases from forms
- Mirror data across platforms

### Process Automation
- Invoice generation and sending
- Order processing workflows
- Report generation and distribution

### Monitoring and Alerts
- Track metrics and KPIs
- Send notifications on events
- Monitor service health

### Content Management
- Publish content across platforms
- Backup and archive data
- Media file processing

## Understanding Operations

Make uses **operations** to measure usage:
- Each module execution = 1 operation
- Data bundles processed individually
- Monitor operation consumption

### Operation Optimization

1. **Use Filters**: Stop unnecessary executions
2. **Aggregate Data**: Process in batches
3. **Set Limits**: Control max executions
4. **Schedule Wisely**: Run when needed

## Best Practices

### Scenario Design

1. **Start Simple**: Build incrementally
2. **Use Modules Efficiently**: Minimize operations
3. **Add Error Handling**: Prepare for failures
4. **Document Your Work**: Add notes to modules

### Data Mapping

1. **Validate Inputs**: Check data before processing
2. **Use Default Values**: Handle missing data
3. **Format Consistently**: Standardize data structure
4. **Test Edge Cases**: Verify all scenarios

### Performance

1. **Optimize Filters**: Filter early in the flow
2. **Limit Iterations**: Process only necessary items
3. **Use Webhooks**: Choose instant triggers when possible
4. **Bundle Operations**: Combine related actions

## Make Interface Overview

### Main Components

1. **Scenario Editor**: Visual workflow builder
2. **Module Panel**: Available apps and tools
3. **Execution History**: Past run logs
4. **Connections**: Manage app authorizations
5. **Organizations**: Team management

### Module Configuration

- **General Settings**: Basic module options
- **Advanced Settings**: Detailed controls
- **Mapping Panel**: Connect data fields
- **Filter Settings**: Add conditions`,
  duration: '90 minutes',
  order: 1,
  objectives: [
    'Understand Make\'s visual automation approach',
    'Learn about scenarios and modules',
    'Create your first visual workflow',
    'Map data between applications',
    'Understand operations and usage'
  ],
  keyConcepts: [
    'Scenarios and modules',
    'Visual workflow design',
    'Data mapping',
    'Operations counting',
    'Trigger types'
  ]
};
